/**
 * uiHelpers — small, shared UI utilities used across LodgiFlow.
 *
 * Includes:
 *  - useEscapeClose(onClose, enabled?)          F5: Escape closes anything
 *  - useCopyToClipboard()                        F4: copy helper
 *  - formatTenantTime(iso, opts?)                F8: tenant-timezone-aware
 *  - formatRelative(iso)                         F8: "3m ago" style
 *  - narrativeDelta(current, previous, opts)     A1: plain-English delta
 *  - detectPersonaDefaults(role, permissions)    A2: default widget layout
 */
import { useEffect, useState, useCallback } from 'react';

// ── F5. Universal escape-to-close hook ──────────────────────────────
export function useEscapeClose(onClose, enabled = true) {
  useEffect(() => {
    if (!enabled || typeof onClose !== 'function') return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, enabled]);
}

// ── F4. Copy-to-clipboard hook ──────────────────────────────────────
export function useCopyToClipboard(resetMs = 1500) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(text ?? ''));
      } else {
        const ta = document.createElement('textarea');
        ta.value = String(text ?? '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), resetMs);
      return true;
    } catch (e) {
      console.warn('[copy] failed', e);
      return false;
    }
  }, [resetMs]);
  return { copied, copy };
}

// ── F8. Tenant-timezone aware time formatting ───────────────────────
// Reads timezone from localStorage (`lodgiflow_tenant_tz`) or the user's
// profile object cached at `lodgiflow_user`. Falls back to browser.
export function getTenantTimezone() {
  try {
    const tz = localStorage.getItem('lodgiflow_tenant_tz');
    if (tz) return tz;
    const raw = localStorage.getItem('lodgiflow_user');
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.timezone) return u.timezone;
    }
  } catch {}
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function formatTenantTime(iso, opts = {}) {
  if (!iso) return '';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    if (Number.isNaN(d.getTime())) return '';
    const tz = getTenantTimezone();
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: opts.dateStyle || 'medium',
      timeStyle: opts.timeStyle || 'short',
      timeZone: tz,
      ...(opts.override || {}),
    }).format(d);
  } catch {
    return String(iso);
  }
}

export function formatRelative(iso) {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const diff = Math.round((d.getTime() - now) / 1000);
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (abs < 60) return rtf.format(diff, 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 30 * 86400) return rtf.format(Math.round(diff / 86400), 'day');
  if (abs < 365 * 86400) return rtf.format(Math.round(diff / (30 * 86400)), 'month');
  return rtf.format(Math.round(diff / (365 * 86400)), 'year');
}

// ── A1. Narrative delta annotation ──────────────────────────────────
// Turn a raw (current, previous) pair into a short, human-readable
// sentence for a KPI card. Never blocks rendering; returns null when
// there's no meaningful signal to show.
export function narrativeDelta(current, previous, {
  label = 'vs. last period',
  unit = '',
  invertColor = false,
  formatValue,
} = {}) {
  if (current == null || previous == null) return null;
  const c = Number(current);
  const p = Number(previous);
  if (Number.isNaN(c) || Number.isNaN(p)) return null;
  const diff = c - p;
  if (p === 0 && diff === 0) return { direction: 'flat', pct: 0, label, sentence: `No change ${label}.` };
  const pct = p === 0 ? null : (diff / Math.abs(p)) * 100;
  const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const fmt = (v) => (formatValue ? formatValue(v) : `${v.toLocaleString()}${unit}`);
  const pctText = pct == null ? '' : ` (${pct > 0 ? '+' : ''}${pct.toFixed(pct >= 10 || pct <= -10 ? 0 : 1)}%)`;
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';
  const positive = invertColor ? direction === 'down' : direction === 'up';
  const sentence = direction === 'flat'
    ? `Steady ${label}.`
    : `${arrow} ${fmt(Math.abs(diff))}${pctText} ${label}.`;
  return {
    direction,
    pct,
    diff,
    label,
    sentence,
    tone: direction === 'flat' ? 'neutral' : positive ? 'positive' : 'negative',
  };
}

// ── A2. Persona-adaptive dashboard defaults ─────────────────────────
// Rough mapping: exec-heavy roles get a light widget mix, ops/IT get
// the deeper stack. Users can still override in the widget customizer.
export function detectPersonaDefaults(role, permissions = []) {
  const perms = new Set(Array.isArray(permissions) ? permissions : []);
  const isExec = role === 'super_admin' || perms.has('view_financials') || perms.has('billing.view');
  const isIT = perms.has('admin.approvals') || perms.has('edit_platforms') || perms.has('edit_products');
  const isOps = !isExec && !isIT;

  const persona = isExec ? 'executive' : isIT ? 'it' : 'ops';

  const layouts = {
    executive: {
      widgets: ['coverage_heatmap', 'ask_stack', 'vendor_watch_summary', 'renewal_radar', 'kpi_row'],
      density: 'comfortable',
      description: 'Portfolio-wide health at a glance',
    },
    it: {
      widgets: ['coverage_heatmap', 'ask_stack', 'integration_health', 'vendor_watch_summary', 'kpi_row'],
      density: 'compact',
      description: 'Deep stack + integration signals',
    },
    ops: {
      widgets: ['coverage_heatmap', 'ask_stack', 'recent_views', 'kpi_row'],
      density: 'comfortable',
      description: 'Day-to-day property operations',
    },
  };

  return { persona, ...layouts[persona] };
}
